import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/** 与 `.workspace-plan-step` 的 `scroll-margin-top` 保持一致。 */
export const PLAN_FOLLOW_OFFSET_PX = 8;
/** 焦点步骤顶边进入容器顶部该容差内即视为可见，避免亚像素抖动触发重复滚动。 */
const VISIBLE_TOLERANCE_PX = 4;
/** 距底部该容差内视为用户主动回到最新位置。 */
const BOTTOM_TOLERANCE_PX = 8;

const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);

export interface PlanFollowScrollOptions {
  /** 计划面板的滚动容器。 */
  containerRef: RefObject<HTMLElement | null>;
  /** 当前焦点步骤；只有焦点步骤会挂载该 ref。 */
  stepRef: RefObject<HTMLElement | null>;
  /** 焦点标识；变化即重新定位。必须是标量，避免每次 runtime 事件都重跑。 */
  focusKey: string;
  /** 是否允许跟随，例如历史计划视图下不跟随。 */
  enabled: boolean;
  /** 会话或计划变化时重新武装跟随并回到瞬时定位。 */
  resetKey: string;
}

export interface PlanFollowScroll {
  following: boolean;
  resumeFollowing: () => void;
  /** 面板下方还有未展示的内容：由调用方渲染淡出提示，避免边界把整行截成半个字。 */
  hasMore: boolean;
}

/**
 * 把正在执行的步骤标题保持在计划面板可视区顶部。
 *
 * 只有真实输入事件（滚轮、触摸、指针、滚动按键）会暂停跟随；程序滚动不会，
 * 因此不需要在 scroll 事件里区分滚动来源。
 */
export function usePlanFollowScroll({
  containerRef,
  stepRef,
  focusKey,
  enabled,
  resetKey,
}: PlanFollowScrollOptions): PlanFollowScroll {
  const [following, setFollowing] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const followingRef = useRef(true);
  const positionedRef = useRef(false);
  const forceRef = useRef(true);

  const measureOverflow = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const scrollable = container.scrollHeight - container.clientHeight;
    const remaining = scrollable - container.scrollTop;
    const next = scrollable > BOTTOM_TOLERANCE_PX && remaining > BOTTOM_TOLERANCE_PX;
    setHasMore((current) => (current === next ? current : next));
  }, [containerRef]);

  const applyFollowing = useCallback((value: boolean) => {
    followingRef.current = value;
    setFollowing(value);
  }, []);

  const align = useCallback(() => {
    const container = containerRef.current;
    const step = stepRef.current;
    if (!container || !step) return;
    const containerRect = container.getBoundingClientRect();
    const stepRect = step.getBoundingClientRect();
    const visible =
      stepRect.top >= containerRect.top + VISIBLE_TOLERANCE_PX &&
      stepRect.bottom <= containerRect.bottom;
    if (visible && !forceRef.current) return;
    forceRef.current = false;
    const behavior: ScrollBehavior = prefersReducedMotion()
      ? "auto"
      : positionedRef.current
        ? "smooth"
        : "auto";
    positionedRef.current = true;
    const top = container.scrollTop + (stepRect.top - containerRect.top) - PLAN_FOLLOW_OFFSET_PX;
    container.scrollTo?.({ top: Math.max(0, top), behavior });
  }, [containerRef, stepRef]);

  // 新会话或新计划重新武装跟随，并让下一次定位使用瞬时滚动。
  // 必须声明在定位效果之前，否则挂载时会覆盖首次定位的记账。
  useEffect(() => {
    positionedRef.current = false;
    forceRef.current = true;
    applyFollowing(true);
  }, [applyFollowing, resetKey]);

  // 焦点步骤推进或切换计划时定位；暂停状态下不抢用户位置。
  useEffect(() => {
    if (!enabled || !followingRef.current) return;
    align();
  }, [align, enabled, focusKey]);

  const resumeFollowing = useCallback(() => {
    forceRef.current = true;
    applyFollowing(true);
    align();
  }, [align, applyFollowing]);

  // 真实用户滚动意图即暂停跟随。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const pause = () => applyFollowing(false);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) pause();
    };
    container.addEventListener("wheel", pause, { passive: true });
    container.addEventListener("touchmove", pause, { passive: true });
    container.addEventListener("pointerdown", pause);
    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("wheel", pause);
      container.removeEventListener("touchmove", pause);
      container.removeEventListener("pointerdown", pause);
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [applyFollowing, containerRef]);

  // 用户滚回底部即恢复跟随，但不立刻重新对齐，保留用户当前阅读位置。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (container.scrollHeight - container.clientHeight <= BOTTOM_TOLERANCE_PX) return;
      if (
        container.scrollTop + container.clientHeight <
        container.scrollHeight - BOTTOM_TOLERANCE_PX
      )
        return;
      applyFollowing(true);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [applyFollowing, containerRef]);

  // 滚动、容器尺寸或内容变化都重新判断是否还有更多内容。
  useEffect(() => {
    const container = containerRef.current;
    measureOverflow();
    if (!container) return;
    container.addEventListener("scroll", measureOverflow, { passive: true });
    window.addEventListener("resize", measureOverflow);
    const observer = new MutationObserver(measureOverflow);
    observer.observe(container, { childList: true, subtree: true });
    // 面板高度会随文件树折叠等操作变化（jsdom 没有 ResizeObserver，需容忍缺失）。
    const resizeObserver =
      typeof ResizeObserver === "function" ? new ResizeObserver(measureOverflow) : null;
    resizeObserver?.observe(container);
    return () => {
      container.removeEventListener("scroll", measureOverflow);
      window.removeEventListener("resize", measureOverflow);
      observer.disconnect();
      resizeObserver?.disconnect();
    };
  }, [containerRef, focusKey, measureOverflow, resetKey]);

  return { following, hasMore, resumeFollowing };
}

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
